import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import DieMiniature from './DieMiniature';
import { DEFAULT_DIE } from './dice';
import { observeElementSize } from '../../utils/observeElementSize';
import { createPortalFrame } from './portalFrames';
import { getPortalVisualKind } from './portalProfiles';
import { createPortalSurface, portalVertexShader } from './portalSurfaces';
import {
  createPortalTunnel,
  createTravelDie,
  PORTAL_ENTRY_MS,
  getPortalTransitDuration,
} from './portalTravel';
import {
  getPortalWarpProfile,
  getPortalWarpProgress,
} from './portalWarpProfiles';
import PortalWarpFallback from './PortalWarpFallback';

const makePortal = (route, index, count) => {
  const group = new THREE.Group();
  const color = new THREE.Color(route.color || '#79e9ff');
  const frame = createPortalFrame(route);
  group.add(frame.group);
  const aperture = createPortalSurface(route, index);
  group.add(aperture);
  const aura = new THREE.Mesh(
    new THREE.PlaneGeometry(3.8, 5.3),
    new THREE.ShaderMaterial({
      vertexShader: portalVertexShader,
      fragmentShader: `
      varying vec2 vUv;
      uniform vec3 tint;
      void main() {
        float radius = length((vUv - 0.5) * 2.0);
        float glow = exp(-pow((radius - 0.72) * 7.0, 2.0)) * 0.13;
        gl_FragColor = vec4(tint, glow);
      }
    `,
      uniforms: { tint: { value: color } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
  );
  aura.position.z = -0.15;
  aura.visible = !route.locked;
  group.add(aura);
  const light = new THREE.PointLight(color, route.locked ? 0.4 : 2, 8, 2);
  light.position.set(0, -0.5, 1);
  group.add(light);
  group.position.set(
    count === 1 ? 0 : (index - (count - 1) / 2) * 5.6,
    0.6,
    index === 1 ? -0.4 : 0
  );
  group.rotation.y = count === 1 ? 0 : (1 - index) * 0.12;
  return { group, aperture, frame, route, light, baseX: group.position.x };
};

// DOM controls remain available when WebGL is unavailable or context is lost.
const PortalScene = ({
  routes,
  opening = false,
  reduceMotion,
  activeId,
  transit = false,
  dieId = DEFAULT_DIE,
  showLabels = false,
  onSelect,
}) => {
  const hostRef = useRef(null);
  const activeRef = useRef(activeId);
  const selectRef = useRef(onSelect);
  const labelRefs = useRef([]);
  const [fallback, setFallback] = useState(false);
  activeRef.current = activeId;
  selectRef.current = onSelect;

  useEffect(() => {
    const host = hostRef.current;
    const warpProfile = getPortalWarpProfile(routes[0]);
    const transitDuration = getPortalTransitDuration(routes[0]);
    const coreWarp = warpProfile.id === 'core';
    const wardenWarp = warpProfile.id === 'warden';
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: transit ? 'high-performance' : 'low-power',
      });
    } catch {
      setFallback(true);
      return undefined;
    }
    setFallback(false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    host.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#02060c');
    scene.fog = new THREE.FogExp2('#02060c', transit ? 0.018 : 0.025);
    const camera = new THREE.PerspectiveCamera(48, 1, 0.1, 160);
    scene.add(new THREE.HemisphereLight('#a4dfff', '#06101a', 2));
    const keyLight = new THREE.DirectionalLight('#e5f4ff', 4.5);
    keyLight.position.set(3, 6, 8);
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight('#70bbff', 2);
    rimLight.position.set(-6, 2, -1);
    scene.add(rimLight);
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(90, 90),
      new THREE.MeshStandardMaterial({
        color: '#07121f',
        metalness: 0.45,
        roughness: 0.4,
      })
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.55;
    scene.add(floor);
    const grid = new THREE.GridHelper(90, 60, '#23445f', '#102538');
    grid.material.transparent = true;
    grid.material.opacity = 0.32;
    grid.position.y = -1.54;
    scene.add(grid);
    const portals = routes.map((route, index) =>
      makePortal(route, index, routes.length)
    );
    portals.forEach(({ group }) => scene.add(group));
    const starPositions = new Float32Array(180 * 3);
    for (let i = 0; i < starPositions.length; i += 3) {
      starPositions[i] = (Math.random() - 0.5) * 60;
      starPositions[i + 1] = Math.random() * 18 - 1;
      starPositions[i + 2] = -Math.random() * 45;
    }
    const starsGeometry = new THREE.BufferGeometry();
    starsGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(starPositions, 3)
    );
    const stars = new THREE.Points(
      starsGeometry,
      new THREE.PointsMaterial({
        color: '#8cbbdf',
        size: 0.045,
        transparent: true,
        opacity: 0.6,
      })
    );
    scene.add(stars);
    const cube =
      opening || transit ? createTravelDie(dieId) : new THREE.Group();
    cube.position.set(0, 0.5, 3);
    cube.visible = opening || transit;
    scene.add(cube);
    const tunnel = transit ? createPortalTunnel(routes[0]) : null;
    if (tunnel) scene.add(tunnel.group);
    const trailPositions = new Float32Array(48 * 3);
    const trailGeometry = new THREE.BufferGeometry();
    trailGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(trailPositions, 3)
    );
    const trail = new THREE.Line(
      trailGeometry,
      new THREE.LineBasicMaterial({
        color: warpProfile.secondary,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    trail.frustumCulled = false;
    scene.add(trail);
    const shockwave = new THREE.Mesh(
      new THREE.RingGeometry(0.96, 1, 96),
      new THREE.MeshBasicMaterial({
        color: '#9cefff',
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    shockwave.position.set(0, 0.65, 0.3);
    scene.add(shockwave);
    const pointer = new THREE.Vector2();
    const raycaster = new THREE.Raycaster();
    let hovered = null;
    let width = 1;
    let height = 1;
    let frame;
    let stopped = false;
    const resize = () => {
      width = Math.max(1, host.clientWidth);
      height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const point = (event) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
      raycaster.setFromCamera(pointer, camera);
      hovered =
        raycaster.intersectObjects(portals.map(({ aperture }) => aperture))[0]
          ?.object.userData.route || null;
      renderer.domElement.style.cursor =
        hovered &&
        (!hovered.locked || hovered.canPurchase) &&
        !opening &&
        !transit
          ? 'pointer'
          : 'default';
    };
    const click = (event) => {
      point(event);
      if (
        !opening &&
        !transit &&
        hovered &&
        (!hovered.locked || hovered.canPurchase)
      )
        selectRef.current?.(hovered.id);
    };
    const leave = () => {
      hovered = null;
      pointer.set(0, 0);
    };
    const contextLost = (event) => {
      event.preventDefault();
      stopped = true;
      cancelAnimationFrame(frame);
      setFallback(true);
    };
    renderer.domElement.addEventListener('pointermove', point);
    renderer.domElement.addEventListener('pointerleave', leave);
    renderer.domElement.addEventListener('click', click);
    renderer.domElement.addEventListener('webglcontextlost', contextLost);
    const stopObservingSize = observeElementSize(host, resize);
    // Shader compilation must not consume the entry animation or desync it
    // from the parent transit's sound and completion clock.
    let start = null;
    const labelAnchor = new THREE.Vector3();
    const flightPosition = new THREE.Vector3();
    const entrySeconds = PORTAL_ENTRY_MS / 1000;
    const flightPoint = (seconds, distance, target) => {
      const launch = THREE.MathUtils.clamp(
        (seconds - 0.45) / (entrySeconds - 0.45),
        0,
        1
      );
      const eased = launch * launch;
      target.set(
        Math.sin(launch * Math.PI) * 1.15,
        THREE.MathUtils.lerp(-0.3, 0.65, eased) +
          Math.sin(launch * Math.PI) * 0.3,
        THREE.MathUtils.lerp(distance - 3.5, -0.5, eased)
      );
      if (seconds > entrySeconds) {
        const flight = getPortalWarpProgress(seconds, transitDuration);
        const bend = Math.sin(flight * Math.PI);
        target.set(
          Math.sin(flight * 8) * warpProfile.sway * bend,
          0.65 + Math.sin(flight * 6) * warpProfile.sway * 0.7 * bend,
          -0.5 -
            (flight * 0.2 + flight * flight * 0.8) *
              (coreWarp ? 120 : wardenWarp ? 86 : 48)
        );
      }
      return target;
    };
    const render = () => {
      if (stopped) return;
      const elapsed = start === null ? 0 : (performance.now() - start) / 1000;
      const time = reduceMotion ? 3 : elapsed;
      const introProgress =
        opening && !reduceMotion ? Math.min(1, elapsed / 6.5) : 1;
      const corePreview =
        !transit &&
        !opening &&
        routes.length === 1 &&
        routes[0].kind === 'Core';
      const wardenPreview =
        !transit && !opening && routes.length === 1 && wardenWarp;
      const portalScaleY =
        corePreview || wardenPreview
          ? wardenPreview
            ? 1.18
            : 1.3
          : Math.min(2.35, Math.max(1, 2.8 / camera.aspect));
      const widthFitDistance =
        8.9 / (Math.tan(THREE.MathUtils.degToRad(24)) * camera.aspect);
      const centerY = transit
        ? 0.65
        : opening
        ? 1.05
        : -1.55 + 2.12 * portalScaleY;
      const distance = transit
        ? Math.max(
            9.8,
            3 / (Math.tan(THREE.MathUtils.degToRad(24)) * camera.aspect)
          )
        : opening
        ? 7.6 + (1 - introProgress) * 10
        : corePreview || wardenPreview
        ? Math.max(
            (corePreview ? 3.25 : 3.2) / Math.tan(THREE.MathUtils.degToRad(24)),
            (corePreview ? 2 : 2.1) /
              (Math.tan(THREE.MathUtils.degToRad(24)) * camera.aspect)
          )
        : Math.max(6.4, widthFitDistance);
      camera.position.set(
        reduceMotion || transit ? 0 : pointer.x * 0.1,
        centerY + (transit ? 0 : 0.25),
        distance
      );
      camera.lookAt(0, centerY, -0.5);
      if (transit && reduceMotion) {
        floor.visible = false;
        grid.visible = false;
        stars.visible = false;
        cube.position.set(0, 0.65, -0.5);
        cube.rotation.set(0.2, 0.4, 0);
        tunnel.update(3, 0.4, camera.position.z, cube.position.z);
      }
      if (transit && !reduceMotion) {
        const pull = THREE.MathUtils.smoothstep(elapsed, 0.65, entrySeconds);
        const passage = getPortalWarpProgress(elapsed, transitDuration);
        flightPoint(elapsed, distance, cube.position);
        camera.position.set(
          cube.position.x * 0.65 * pull,
          THREE.MathUtils.lerp(centerY, cube.position.y, pull * 0.6),
          THREE.MathUtils.lerp(
            distance,
            cube.position.z + (coreWarp ? 7 : 6.3),
            pull
          )
        );
        camera.up.set(Math.sin(passage * Math.PI) * warpProfile.bank, 1, 0);
        camera.lookAt(cube.position.x * 0.5, centerY, camera.position.z - 24);
        camera.fov =
          48 +
          Math.sin(passage * Math.PI) * (coreWarp ? 28 : wardenWarp ? 26 : 20);
        camera.updateProjectionMatrix();
        floor.visible = elapsed < entrySeconds + 0.1;
        grid.visible = floor.visible;
        stars.visible = floor.visible;
        tunnel.update(elapsed, passage, camera.position.z, cube.position.z);
        cube.rotation.set(time * 0.7, time * 1.25, time * 0.45);
        const arrival = THREE.MathUtils.smoothstep(
          elapsed,
          transitDuration / 1000 - 0.35,
          transitDuration / 1000
        );
        cube.scale.setScalar((0.8 + pull * 0.2) * (1 - arrival));
        trail.material.opacity = elapsed > 0.45 ? 0.75 * (1 - arrival) : 0;
        for (let i = 0; i < 48; i += 1) {
          flightPoint(
            Math.max(0, elapsed - i * 0.012),
            distance,
            flightPosition
          );
          flightPosition.toArray(trailPositions, i * 3);
        }
        trailGeometry.attributes.position.needsUpdate = true;
        const impact = THREE.MathUtils.clamp((elapsed - 1.5) / 0.65, 0, 1);
        shockwave.scale.set(1 + impact * 8, 1.5 + impact * 8, 1);
        shockwave.material.opacity =
          elapsed > 1.5 ? Math.sin(impact * Math.PI) * 0.6 : 0;
      }
      portals.forEach(
        (
          { group, aperture, frame: portalFrame, route, light, baseX },
          index
        ) => {
          const selected = route.id === (hovered?.id || activeRef.current);
          const charge = transit
            ? reduceMotion
              ? 1
              : THREE.MathUtils.smoothstep(elapsed, 0.4, entrySeconds)
            : 0;
          const reveal =
            route.newlyStable &&
            showLabels &&
            !transit &&
            !opening &&
            !reduceMotion
              ? 1 - THREE.MathUtils.smoothstep(elapsed, 0, 2.8)
              : 0;
          const revealPulse = reveal * (0.5 + 0.5 * Math.sin(elapsed * 9) ** 2);
          aperture.material.uniforms.time.value =
            time * (1 + charge * 1.5) + index * 4;
          aperture.material.uniforms.charge.value = charge;
          aperture.material.uniforms.passage.value = transit
            ? reduceMotion
              ? 1
              : THREE.MathUtils.smoothstep(
                  elapsed,
                  entrySeconds - 0.1,
                  entrySeconds + 0.3
                )
            : 0;
          portalFrame.update(
            time,
            selected || transit || revealPulse > 0.04,
            charge + revealPulse * 0.8
          );
          light.intensity = route.locked
            ? 0.4
            : (selected ? 4 : 2) + charge * 6 + revealPulse * 8;
          if (transit) group.scale.setScalar(1.25 + charge * 0.06);
          else if (opening)
            group.scale.setScalar(
              reduceMotion
                ? 1.3
                : Math.min(1.3, Math.max(0.01, (elapsed - 0.5) / 1.7))
            );
          else {
            group.scale.set(1.1, portalScaleY, 1.1);
            // Keep each portal above its card while fitting the full portal frame.
            group.position.x = baseX * (distance / widthFitDistance);
          }
          group.position.y = centerY;
        }
      );
      if (opening) {
        cube.rotation.set(time * 0.3, time * 0.7, time * 0.15);
        cube.position.y = 0.5 + Math.sin(time * 1.4) * 0.14;
        cube.scale.setScalar(
          Math.max(0.01, 1 - Math.max(0, introProgress - 0.6) * 2.5)
        );
      }
      renderer.render(scene, camera);
      if (start === null) start = performance.now();
      if (showLabels) {
        portals.forEach(({ group }, index) => {
          const label = labelRefs.current[index];
          if (!label) return;
          labelAnchor.set(0, -2.4, 0);
          group.localToWorld(labelAnchor);
          labelAnchor.project(camera);
          // Keep the complete name (and locked-state line) inside short scenes.
          const halfWidth = label.offsetWidth / 2;
          label.style.left = `${THREE.MathUtils.clamp(
            (labelAnchor.x * 0.5 + 0.5) * width,
            halfWidth + 8,
            Math.max(halfWidth + 8, width - halfWidth - 8)
          )}px`;
          label.style.top = `${THREE.MathUtils.clamp(
            (-labelAnchor.y * 0.5 + 0.5) * height,
            8,
            Math.max(8, height - label.offsetHeight - 8)
          )}px`;
        });
      }
      frame = requestAnimationFrame(render);
    };
    render();
    return () => {
      stopped = true;
      cancelAnimationFrame(frame);
      stopObservingSize();
      renderer.domElement.removeEventListener('pointermove', point);
      renderer.domElement.removeEventListener('pointerleave', leave);
      renderer.domElement.removeEventListener('click', click);
      renderer.domElement.removeEventListener('webglcontextlost', contextLost);
      const geometries = new Set();
      const materials = new Set();
      scene.traverse((object) => {
        if (object.isInstancedMesh) object.dispose();
        if (object.geometry) geometries.add(object.geometry);
        if (object.material)
          (Array.isArray(object.material)
            ? object.material
            : [object.material]
          ).forEach((material) => materials.add(material));
      });
      geometries.forEach((geometry) => geometry.dispose());
      const textures = new Set();
      materials.forEach((material) => {
        if (material.map) textures.add(material.map);
        material.dispose();
      });
      textures.forEach((texture) => texture.dispose());
      renderer.dispose();
      renderer.forceContextLoss?.();
      renderer.domElement.remove();
    };
  }, [routes, opening, reduceMotion, transit, dieId, showLabels]);

  return (
    <div
      ref={hostRef}
      className={`coreboundPortalCanvas ${fallback ? 'is-fallback' : ''} ${
        reduceMotion ? 'is-reduced-motion' : ''
      }`}
      aria-hidden="true"
      data-warp-profile={
        transit ? getPortalWarpProfile(routes[0]).id : undefined
      }
      data-portal-profile={getPortalWarpProfile(routes[0]).id}
    >
      {fallback && transit && (
        <PortalWarpFallback
          profile={getPortalWarpProfile(routes[0])}
          dieId={dieId}
        />
      )}
      {fallback && !transit && (
        <div className="coreboundPortalFallback">
          {routes.map((route) => (
            <i
              key={route.id}
              data-kind={getPortalVisualKind(route)}
              className={`${route.locked ? 'is-locked' : ''} ${
                route.newlyStable && showLabels && !transit && !opening
                  ? 'is-newly-stable'
                  : ''
              }`}
              style={{ '--portal-color': route.color || '#79e9ff' }}
            />
          ))}
          {(opening || transit) && (
            <span className="portalFallbackCube">
              <DieMiniature dieId={dieId} />
            </span>
          )}
        </div>
      )}
      {showLabels && (
        <div
          className={`portalSceneLabels ${fallback ? 'is-fallback' : ''} ${
            reduceMotion ? 'is-reduced-motion' : ''
          }`}
        >
          {routes.map((route, index) => (
            <span
              key={route.id}
              ref={(node) => {
                labelRefs.current[index] = node;
              }}
              className={`portalSceneLabel ${
                route.newlyStable && !transit && !opening
                  ? 'is-newly-stable'
                  : ''
              }`}
              style={{
                '--portal-label-width': `${92 / routes.length}%`,
                '--portal-label-left': `${
                  ((index + 0.5) * 100) / routes.length
                }%`,
              }}
            >
              <strong>{getPortalVisualKind(route) || 'The Breach'}</strong>
              {route.locked && <strong>UNSTABLE</strong>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalScene;
